/**
 * 用户服务
 * 处理用户相关的业务逻辑
 */

import type { HttpClient } from '../http/HttpClient'
import type { IStorage } from '../storage/Storage'
import type {
  UserInfo,
  VipInfo,
  PointsInfo,
  TempPointListParams,
  TempPointListResult,
  TempPointItem,
  PointsLogParams,
  PointsLogResult,
  Task,
  SignInResponse,
  FeedbackParams,
  FeedbackResponse
} from '../../types'
import { UserAPI } from '../api/user'
import { UserStore } from '../stores/UserStore'
import { BaseService } from './BaseService'

export class UserService extends BaseService {
  private userAPI: UserAPI
  private userStore: UserStore

  constructor(httpClient: HttpClient, storage: IStorage) {
    super(httpClient, storage)
    this.userAPI = new UserAPI(httpClient)
    this.userStore = new UserStore(storage)
  }

  /**
   * 获取 UserStore 实例
   */
  getUserStore(): UserStore {
    return this.userStore
  }

  /**
   * 获取用户信息
   */
  async getUserInfo(): Promise<UserInfo> {
    try {
      const userInfo = await this.userAPI.getUserInfo()
      this.userStore.userInfo = userInfo
      return userInfo
    } catch (error) {
      console.error('[UserService] Failed to get user info', error)
      throw error
    }
  }

  /**
   * 获取 VIP 信息
   */
  async getVipInfo(): Promise<VipInfo> {
    try {
      const vipInfo = await this.userAPI.getVipInfo()
      this.userStore.vipInfo = vipInfo
      return vipInfo
    } catch (error) {
      console.error('[UserService] Failed to get VIP info', error)
      throw error
    }
  }

  /**
   * 获取积分信息
   */
  async getPointsInfo(): Promise<PointsInfo> {
    try {
      const pointsInfo = await this.userAPI.getPointsInfo()
      this.userStore.pointsInfo = pointsInfo
      return pointsInfo
    } catch (error) {
      console.error('[UserService] Failed to get points info', error)
      throw error
    }
  }

  /**
   * 获取临时积分列表
   */
  async getTempPointList(params: TempPointListParams = {}): Promise<TempPointListResult> {
    try {
      return await this.userAPI.getTempPointList(params)
    } catch (error) {
      console.error('[UserService] Failed to get temp point list', error)
      throw error
    }
  }

  /**
   * 获取单个临时积分记录详情
   */
  async getTempPointDetail(id: number): Promise<TempPointItem> {
    try {
      return await this.userAPI.getTempPointDetail(id)
    } catch (error) {
      console.error('[UserService] Failed to get temp point detail', error)
      throw error
    }
  }

  /**
   * 查询积分流水
   */
  async getPointsLog(params: PointsLogParams = {}): Promise<PointsLogResult> {
    try {
      return await this.userAPI.getPointsLog(params)
    } catch (error) {
      console.error('[UserService] Failed to get points log', error)
      throw error
    }
  }

  /**
   * 查询临时积分流水，固定 source_type=1
   */
  async getTempPointLogs(params: PointsLogParams = {}): Promise<PointsLogResult> {
    try {
      return await this.userAPI.getTempPointLogs(params)
    } catch (error) {
      console.error('[UserService] Failed to get temp point logs', error)
      throw error
    }
  }

  /**
   * 获取任务列表
   */
  async getTasks(): Promise<Task[]> {
    try {
      const tasks = await this.userAPI.getTasks()
      this.userStore.tasks = tasks
      return tasks
    } catch (error) {
      console.error('[UserService] Failed to get tasks', error)
      throw error
    }
  }

  /**
   * 签到
   */
  async signIn(): Promise<SignInResponse> {
    try {
      const result = await this.userAPI.signIn()

      // 签到成功后更新积分
      if (result.data) {
        await this.getPointsInfo()
      }

      return result
    } catch (error) {
      console.error('[UserService] Failed to sign in', error)
      throw error
    }
  }

  /**
   * 提交反馈
   */
  async feedback(params: FeedbackParams): Promise<FeedbackResponse> {
    try {
      return await this.userAPI.feedback(params)
    } catch (error) {
      console.error('[UserService] Failed to submit feedback', error)
      throw error
    }
  }

  /**
   * 刷新所有用户数据
   */
  async refreshAll(): Promise<void> {
    try {
      await Promise.all([
        this.getUserInfo(),
        this.getVipInfo(),
        this.getPointsInfo(),
        this.getTasks()
      ])
    } catch (error) {
      console.error('[UserService] Failed to refresh all user data', error)
      throw error
    }
  }

  /**
   * 清除用户数据
   */
  clearUserData(): void {
    this.userStore.clearUserInfo()
  }

  /**
   * 获取当前用户信息（从 store）
   */
  getCurrentUserInfo(): UserInfo | null {
    return this.userStore.userInfo
  }

  /**
   * 获取当前 VIP 信息（从 store）
   */
  getCurrentVipInfo(): VipInfo | null {
    return this.userStore.vipInfo
  }

  /**
   * 获取当前积分信息（从 store）
   */
  getCurrentPointsInfo(): PointsInfo | null {
    return this.userStore.pointsInfo
  }

  /**
   * 获取当前任务列表（从 store）
   */
  getCurrentTasks(): Task[] {
    return this.userStore.tasks
  }

  /**
   * 是否是 VIP
   */
  isVip(): boolean {
    return this.userStore.isVip.value
  }

  /**
   * VIP 是否过期
   */
  isVipExpired(): boolean {
    return this.userStore.isVipExpired.value
  }

  /**
   * VIP 剩余天数
   */
  getVipRemainingDays(): number {
    return this.userStore.vipRemainingDays.value
  }

  /**
   * 可用积分
   */
  getAvailablePoints(): number {
    return this.userStore.availablePoints.value
  }

  /**
   * 绑定手机号
   */
  async bindPhone(params: { phone: string; code: string }): Promise<{ token?: string } | void> {
    try {
      const result = await this.userAPI.bindPhone(params)
      // 绑定成功后刷新用户信息
      await this.getUserInfo()
      return result
    } catch (error) {
      console.error('[UserService] Failed to bind phone', error)
      throw error
    }
  }

  /**
   * 绑定微信（获取二维码）
   */
  async bindWechat() {
    try {
      return await this.userAPI.bindWechat()
    } catch (error) {
      console.error('[UserService] Failed to bind wechat', error)
      throw error
    }
  }

  /**
   * 获取任务调度信息
   */
  async getTaskSchedule() {
    try {
      return await this.userAPI.getTaskSchedule()
    } catch (error) {
      console.error('[UserService] Failed to get task schedule', error)
      throw error
    }
  }

  /**
   * 获取用户积分记录列表
   */
  async getUserPointGrantList(params: Record<string, string | number>) {
    try {
      return await this.userAPI.getUserPointGrantList(params)
    } catch (error) {
      console.error('[UserService] Failed to get user point grant list', error)
      throw error
    }
  }

  /**
   * 获取激活码列表
   */
  async getActivationCodeList() {
    try {
      return await this.userAPI.getActivationCodeList()
    } catch (error) {
      console.error('[UserService] Failed to get activation code list', error)
      throw error
    }
  }
}
